/**
 * useRolesList — TanStack Query over `GET /organizations/:slug/roles`.
 *
 * Powers the role-picker on `/users/invite`. Returns the built-in +
 * custom roles available in the current org.
 *
 * Status union: `loading | ready | forbidden | error`.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { RoleRow } from './types.js';

const FORBIDDEN = Symbol('roles-list:forbidden');

export type UseRolesListResult =
  | { readonly status: 'loading'; readonly refetch: () => void }
  | {
      readonly status: 'ready';
      readonly data: readonly RoleRow[];
      readonly refetch: () => void;
    }
  | { readonly status: 'forbidden'; readonly refetch: () => void }
  | { readonly status: 'error'; readonly refetch: () => void };

interface RoleDto {
  readonly id: string;
  readonly name: string;
  readonly isBuiltIn: boolean;
  readonly organizationId?: string | null;
}

interface RolesEnvelope {
  readonly roles: readonly RoleDto[];
}

function projectRole(dto: RoleDto): RoleRow {
  return { id: dto.id, name: dto.name, isBuiltIn: dto.isBuiltIn };
}

export function buildRolesListQueryKey(
  orgSlug: string | null,
): readonly unknown[] {
  return ['identity', 'roles', orgSlug];
}

export function useRolesList(): UseRolesListResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const enabled = orgSlug !== null;

  const query = useQuery({
    queryKey: buildRolesListQueryKey(orgSlug),
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET('/organizations/{slug}/roles', {
        params: { path: { slug: orgSlug as string } },
      });
      if (result.response.status === 403) return FORBIDDEN;
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/roles failed with status ${result.response.status}`,
        );
      }
      return (result.data as RolesEnvelope).roles.map(projectRole);
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (!enabled || query.isPending) return { status: 'loading', refetch };
  if (query.isError) return { status: 'error', refetch };
  if (query.data === FORBIDDEN) return { status: 'forbidden', refetch };
  return { status: 'ready', data: query.data, refetch };
}
